import type { FWDTemplateChartSeries, TemplateChartSeries } from '@/types/charts';

export type TableWidget = {
  type: 'table';
  name: string;
  definition: {
    type: 'table';
    metadata: null | Record<string, any>;
    tabs: Array<{
      name: string;
      metadata: null | Record<string, any>;
      sections: Array<{
        name: string;
        rows: Array<{
          shortTitle: string;
          symbol: {
            key: {
              Code: string;
              PeriodType: string;
              Period: string;
              TimeRef: string;
            };
            group: string;
          };
          diffSymbol?: {
            key: {
              Code: string;
              PeriodType: string;
              Period: string;
              TimeRef: string;
            };
            group?: string;
          };
        }>;
      }>;
    }>;
  };
};

export type ChartTabDefinition = {
  id: string;
  name: string;
  metadata: null | Record<string, any>;
  series: Array<TemplateChartSeries>;
};

export type SimpleChartWidget = {
  type: 'graph';
  name: string;
  definition: {
    layout: null | Record<string, any>;
    type: 'graph';
    tabs: Array<ChartTabDefinition>;
    metadata: null | Record<string, any>;
  };
};

export type FWDChartWidget = {
  type: 'graph';
  name: string;
  definition: {
    layout: null | Record<string, any>;
    type: 'graph';
    tabs: Array<ChartTabDefinition>;
    metadata: {
      graphType: 'forwardSimple';
    };
  };
};

export type SeasonalChartWidget = {
  type: 'graph';
  name: string;

  definition: {
    layout: null | Record<string, any>;
    type: 'graph';
    tabs: Array<ChartTabDefinition>;
    metadata: {
      graphType: 'seasonal';
      from: string;
    };
  };
};

export type TilesWidget = {
  type: 'tiles';
  name: string;
  definition: any;
};

export type Widget =
  | TableWidget
  | SimpleChartWidget
  | FWDChartWidget
  | SeasonalChartWidget
  | TilesWidget;

/// V2
export type SeasonalChartWidgetV2 = {
  id: string;
  name: string;
  type: 'graph';
  tabs?: Array<ChartTabDefinition>;
  layout?: {
    height?: number;
  } | null;
  metadata: {
    graphType: 'seasonal';
    from: string;
  };
  series: Array<TemplateChartSeries>;
};

export type FWDChartWidgetV2 = {
  id: string;
  name: string;
  type: 'graph';
  tabs?: Array<ChartTabDefinition>;
  layout?: {
    height?: number;
  } | null;
  metadata: {
    graphType: 'forwardSimple';
  };
  series: Array<FWDTemplateChartSeries>;
};

export type SimpleChartWidgetV2 = {
  id: string;
  name: string;
  type: 'graph';
  tabs?: Array<ChartTabDefinition>;
  layout?: {
    height?: number;
  } | null;
  metadata: Record<string, string> | null;
  series: Array<TemplateChartSeries>;
};

export type FeedWidgetV2 = {
  id: string;
  name: string;
  type: 'feed';
  layout?: {
    height?: number;
  } | null;
  metadata: {
    KeyCategory: string;
    KeyValue: string;
  };
};
export type CSVTableWidgetV2 = {
  id: string;
  name: string;
  type: 'csvtable';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, any>;
  fileName: string;
  groupName: string;
};

export type MiniTableWidgetV2 = {
  id: string;
  name: string;
  type: 'minitable';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, any>;
  tables: Array<{
    name: string;
    layout: {
      height?: number;
    } | null;
    rows: Array<
      | {
          columns: string[];
          metadata: null | Record<string, any>;
          layout: null | Record<string, any>;
          from: string;
        }
      | {
          symbol: {
            key: {
              Code: string;
              PeriodType: string;
              Period: string;
              TimeRef: string;
            };
            group: string;
          };
          diffSymbol?: {
            key: {
              Code: string;
              PeriodType: string;
              Period: string;
              TimeRef: string;
            };
            group?: string;
          };
          from: string;
          columns: string[];
          layout: null | Record<string, any>;
          metadata: null | Record<string, any>;
        }
    >;
  }>;
};

export type TableRow = {
  shortTitle: string;
  symbol: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
  diffSymbol?: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
};

export type TableWidgetV2 = {
  id: string;
  name: string;
  type: 'table';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, any>;
  sections: Array<{
    name: string;
    rows: TableRow[];
  }>;
};

export type SpotHistoryTableItem = {
  name: string;
  symbol: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
};

export type SpotHistoryTableWidgetV2 = {
  id: string;
  name: string;
  type: 'spot_history_table';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, any>;
  items: SpotHistoryTableItem[];
};

export type TileBigNumber = {
  id: string;
  shortTitle: string;
  symbol: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
};
export type TilesWidgetV2 = {
  id: string;
  name: string;
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, any>;
  type: 'tiles';
  tiles: TileBigNumber[];
};

export type FilesWidgetV2 = {
  id: string;
  name: string;
  type: 'file_list';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, any>;
  groupName: string;
  size?: number;
};

export type WidgetV2 =
  | SeasonalChartWidgetV2
  | SimpleChartWidgetV2
  | FWDChartWidgetV2
  | FeedWidgetV2
  | CSVTableWidgetV2
  | MiniTableWidgetV2
  | TableWidgetV2
  | SpotHistoryTableWidgetV2
  | FilesWidgetV2;
export type MarketAppWithTabs = { tabs: WidgetV2[]; id: string };
export type MarketApp = MarketAppWithTabs | TilesWidgetV2;
export type MarketApps = MarketApp[];

// Tab entry with id and content
export type TabEntry = {
  id: string;
  content: MarketApps;
};

// New nested structure types
// Legacy format: { [tabName: string]: MarketApps }
// New format: { [tabName: string]: TabEntry }
export type DashboardTab = {
  [tabName: string]: MarketApps | TabEntry;
};

export type NestedDashboardStructure = {
  [dashboardName: string]: DashboardTab;
};

// Legacy structure type
export type LegacyDashboardStructure = {
  content: MarketApps;
};

// Union type for backward compatibility
export type DashboardStructure = NestedDashboardStructure | LegacyDashboardStructure;

// Type guards for structure detection
export type DashboardResponse = LegacyDashboardStructure | NestedDashboardStructure;

export enum AppType {
  Spot = 'spot',
  FWD = 'forward',
  Seasonal = 'seasonal',
  Table = 'table',
  Tiles = 'tiles',
  Feed = 'feed',
  CSVTable = 'csvtable',
  Minitable = 'minitable',
  SpotHistoryTable = 'spot_history_table'
}
